import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import { TRENT_LOGO_URL } from "@/components/Logo";

export default function AdminLogin() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Clear any stale token on mount (defensive)
  useEffect(() => {
    try { localStorage.removeItem("trent_token"); } catch { /* ignore */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        // Hard fail: this page is admin-only. Sign them out + show error.
        logout();
        setError("This sign-in is for administrators only. Use the Customer Sign In instead.");
        return;
      }
      navigate("/admin-dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:flex relative bg-[#0F172A] text-white p-12 flex-col justify-between overflow-hidden">
        <div className="trent-grid-bg absolute inset-0 opacity-20" />
        <div className="relative">
          <div className="bg-white rounded-md px-4 py-3 inline-flex">
            <img src={TRENT_LOGO_URL} alt="Trent" className="h-12 w-auto" />
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-white/60">
            Admin console · Restricted access
          </div>
        </div>
        <div className="relative">
          <ShieldCheck size={48} className="text-[#16A34A] mb-4" weight="duotone" />
          <h1 className="trent-heading text-4xl font-black tracking-tighter leading-[1.05]">
            Administrator sign in.
          </h1>
          <p className="mt-4 text-white/70 text-sm max-w-md leading-relaxed">
            Internal dashboard for inspectors and managers. Customers should use the standard
            sign-in page.
          </p>
        </div>
        <div className="relative text-[10px] uppercase tracking-[0.3em] text-white/40">
          Trent Property Management · Ireland
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#16A34A] font-semibold mb-3">
            Admin only
          </div>
          <h2 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A] mb-8">
            Admin Sign In
          </h2>

          <label className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="trent-input mt-1 mb-4" required data-testid="admin-email-input" />

          <label className="text-xs font-semibold uppercase tracking-wider text-[#475569]">Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="trent-input mt-1 mb-2" required data-testid="admin-password-input" />

          {error && (
            <div className="my-3 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FCA5A5] px-3 py-2 rounded-sm" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="trent-btn-primary w-full mt-4" data-testid="admin-login-submit">
            {loading ? "Signing in..." : "Sign in as admin"} <ArrowRight weight="bold" size={16} />
          </button>

          <div className="mt-6 text-sm text-[#475569]">
            Are you a customer?{" "}
            <Link to="/login" className="text-[#0F172A] font-semibold underline" data-testid="goto-customer-login">
              Customer Sign In
            </Link>
          </div>
          <div className="mt-3 text-sm text-[#475569]">
            <Link to="/" className="text-[#0F172A] font-semibold underline" data-testid="admin-back-home">
              ← Back to homepage
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
